import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import React, { memo } from 'react';

import { Button } from 'ui-components/components/button';

import { ActionableRateCard } from '../rate-card/actionable/actionable-rate-card';
import ConfigureReturnShipment from './sections/configure-return-shipment/configure-return-shipment';
import CreateReturnLabelButton from './sections/rate-card/actionable/create-return-label-button';
import FulfillmentDisplay from './sections/fulfillment-display/fulfillment-display';
import { Items } from './sections/items/items';
import Notes from './sections/notes/notes';
import { OrderDetailsCanvasHotkeys } from '../../../components/hotkeys/advanced-hotkeys/order-details-canvas-hotkeys';
import { OrderDetailsSelectedContent } from '../selected-content/order-details-selected-content';
import { ReadOnlyRateCardContainer } from './sections/rate-card/read-only/container';
import { ReturnAdapterForFulfillmentPlannerContext } from '../../../fulfillment-planner';
import { SelectReturnItemsModal } from './select-return-items-modal/select-return-items-modal';
import ShipmentDetails from './sections/shipment-details/shipment-details';
import { useCreateReturnContext } from '../context/create-return-provider';
import { usePendingReturnContext } from '../context/pending-return-context-provider';
import { useSalesOrderIdContext } from '../../context/sales-order-id-context-provider';

import messages from './messages';
import style from './details.module.less';

const ReturnDetails = ({ returnFulfillmentId, counter, isNewReturn = false }) => {
  const salesOrderId = useSalesOrderIdContext();
  const { cancelPendingReturn } = usePendingReturnContext();
  const { returnFulfillmentPlanId, shouldPromptUserForReturnItems, updateReturnItems, isInternationalReturn } = useCreateReturnContext();
  const isDisabled = isInternationalReturn;
  const disabledMessage = isInternationalReturn ? messages.internationalReturn : null;

  // pending returns require user item selection
  // before we mount the ReturnDetails view
  if (shouldPromptUserForReturnItems) {
    return <SelectReturnItemsModal onClose={cancelPendingReturn} onContinue={updateReturnItems} salesOrderId={salesOrderId} />;
  }

  const headerContent = isNewReturn && (
    <Button onClick={cancelPendingReturn} theme='tertiary'>
      <FormattedMessage {...messages.cancelReturn} />
    </Button>
  );

  const primaryShipmentSections = (
    <>
      <ShipmentDetails counter={counter} isEditable={isNewReturn} returnFulfillmentId={returnFulfillmentId} />
      <Items isEditable={isNewReturn} returnFulfillmentId={returnFulfillmentId} />
    </>
  );

  const secondaryShipmentSections = <Notes isEditable={isNewReturn} returnFulfillmentId={returnFulfillmentId} />;

  const fulfillmentPlannerSections = isNewReturn ? (
    <>
      <OrderDetailsCanvasHotkeys fulfillmentPlanId={returnFulfillmentPlanId} isReturnView />
      <ConfigureReturnShipment returnFulfillmentPlanId={returnFulfillmentPlanId} />
    </>
  ) : (
    <>
      <ReadOnlyRateCardContainer returnFulfillmentId={returnFulfillmentId} />
      <FulfillmentDisplay returnFulfillmentId={returnFulfillmentId} />
    </>
  );

  return (
    <ReturnAdapterForFulfillmentPlannerContext
      key={returnFulfillmentPlanId}
      fulfillmentPlanId={returnFulfillmentPlanId}
      salesOrderId={salesOrderId}
    >
      <OrderDetailsSelectedContent
        fulfillmentPlannerSections={fulfillmentPlannerSections}
        headerClassName={style['return-header-row']}
        headerContent={headerContent}
        primaryShipmentSections={primaryShipmentSections}
        rateCard={
          isNewReturn && (
            <ActionableRateCard
              ActionButton={CreateReturnLabelButton}
              disabledMessage={disabledMessage}
              hideCostReview
              isDisabled={isDisabled}
            />
          )
        }
        secondaryShipmentSections={secondaryShipmentSections}
      />
    </ReturnAdapterForFulfillmentPlannerContext>
  );
};

export default memo(ReturnDetails);

ReturnDetails.propTypes = {
  returnFulfillmentId: PropTypes.string.isRequired,
  counter: PropTypes.number.isRequired,
  isNewReturn: PropTypes.bool,
};
